import { FormEvent, useState } from 'react';
import { ArrowUp, BrainCircuit, Sparkles } from 'lucide-react';

interface IntelligenceQuickAskProps {
  pendingOpportunities: number;
  onAsk: (question: string) => void;
}

const suggestions = [
  'O que devo priorizar agora?',
  'Por que estas oportunidades combinam comigo?',
  'O que está impedindo minha participação?',
];

/**
 * Entry point intentionally kept short: the Feed helps decide, while the
 * dedicated Intelligence route owns the full conversation.
 */
export function IntelligenceQuickAsk({ pendingOpportunities, onAsk }: IntelligenceQuickAskProps) {
  const [question, setQuestion] = useState('');

  const ask = (value: string) => {
    const normalized = value.trim();
    if (!normalized) return;
    onAsk(normalized);
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    ask(question);
  };

  return (
    <section
      className="rounded-xl border border-ds-brand/30 bg-ds-brand-subtle/30 p-4 shadow-sm"
      aria-label="Pergunte à Inteligência"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-ds-brand/10 p-2 text-ds-brand" aria-hidden="true">
          <BrainCircuit className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="font-semibold text-ds-text-primary">Pergunte à Inteligência</h2>
              <p className="mt-1 text-sm text-ds-text-secondary">
                {pendingOpportunities > 0
                  ? `${pendingOpportunities} oportunidade(s) aguardam sua decisão. Pergunte e veja as fontes na conversa.`
                  : 'Pergunte sobre sua empresa, documentos e oportunidades. A resposta mostra as fontes disponíveis.'}
              </p>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full bg-ds-brand/10 px-2 py-1 text-xs font-medium text-ds-brand">
              <Sparkles className="h-3.5 w-3.5" /> Contexto da sua empresa
            </span>
          </div>

          <form className="mt-3 flex gap-2" onSubmit={submit}>
            <label className="sr-only" htmlFor="quick-intelligence-question">Faça uma pergunta à Inteligência</label>
            <input
              id="quick-intelligence-question"
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              className="min-w-0 flex-1 rounded-lg border border-ds-border bg-ds-surface px-3 py-2 text-sm text-ds-text-primary outline-none transition focus:border-ds-brand focus:ring-2 focus:ring-ds-brand/25"
              placeholder="Ex.: O que preciso conferir antes de decidir?"
            />
            <button
              type="submit"
              aria-label="Iniciar conversa com a Inteligência"
              disabled={!question.trim()}
              className="inline-flex shrink-0 items-center justify-center rounded-lg bg-ds-brand-solid px-3 text-ds-on-brand transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-ds-brand disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ArrowUp className="h-4 w-4" />
            </button>
          </form>

          <div className="mt-3 flex flex-wrap gap-2" aria-label="Perguntas sugeridas">
            {suggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => ask(suggestion)}
                className="rounded-full border border-ds-brand/25 bg-ds-surface px-3 py-1.5 text-xs font-medium text-ds-brand transition hover:bg-ds-brand/10 focus:outline-none focus:ring-2 focus:ring-ds-brand"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
